import React, { Fragment, useState, useEffect, useRef, useCallback } from "react";
import { STATUTORY } from "../../config/statutoryIdentifiers";
import { useLocation, useNavigate, Link } from "react-router-dom";
import SEO from "../../components/SEO/SEO";
import Header from "../../components/Header/Header";
import PageTitle from "../../components/pagetitle/PageTitle";
import Scrollbar from "../../components/scrollbar/scrollbar";
import Footer from "../../components/footer/Footer";
import { useRazorpay } from "../../hooks/useRazorpay";
import { formatPrice, signupTenant, createOrder, getSubscriptionStatus, isRecurringBillingCycle, RecurringBillingCycle } from "../../services/api";
import { useSaleOpen } from "../../hooks/useSaleOpen";
import { usePricingData } from "../../hooks/usePricingData";
import { useVerticalPricing } from "../../hooks/useVerticalPricing";
import { TermsAcceptanceModal, AcceptanceData } from "../../components/TermsAcceptance";
import { loadSignupDraft, clearSignupDraft } from "../../utils/signupDraft";
import { clearVerticalIntent } from "../../utils/verticalIntent";
import { getAttribution } from "../../components/ReferralTracker/ReferralTracker";
import { track } from "../../services/analytics";

// Colorful theme backgrounds
import pricingBg from "../../images/bg/pricing-bg.png";
import cardBg from "../../images/bg/pricing-bg01.png";

interface CheckoutState {
  company_name: string;
  subdomain: string;
  admin_email: string;
  email_verification_token: string;
  admin_password: string;
  admin_name: string;
  phone: string;
  plan: string;
  referral_code: string;
  billing_cycle: "monthly" | "quarterly" | "six_month" | "yearly" | "five_year" | "ten_year";
  primary_vertical: string;
  addon_verticals: string[];
  // Address & GST
  state?: string | null;
  city?: string | null;
  pincode?: string | null;
  address_line?: string | null;
  gstin?: string | null;
  tenant_id?: string;
}

// Provisioning status messages for the loading animation
const PROVISIONING_MESSAGES = [
  { message: "Creating your secure database...", icon: "fa-database" },
  { message: "Building 300+ business tables...", icon: "fa-table" },
  { message: "Configuring AI assistants & industry modules...", icon: "fa-robot" },
  { message: "Setting up accounting, GST & compliance...", icon: "fa-calculator" },
  { message: "Provisioning CRM, HR & inventory...", icon: "fa-cogs" },
  { message: "Running security configurations...", icon: "fa-shield-alt" },
  { message: "Initializing your dashboard...", icon: "fa-tachometer-alt" },
  { message: "Almost ready...", icon: "fa-check-circle" },
];

const CheckoutPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [checkoutData, setCheckoutData] = useState<CheckoutState | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [step, setStep] = useState<"terms" | "creating" | "payment" | "provisioning" | "success" | "error">("terms");
  const [errorMessage, setErrorMessage] = useState<string>("");
  // Which stage the error came from — account creation never touches Razorpay,
  // so it should never be presented to the user as a payment problem.
  const [errorKind, setErrorKind] = useState<"account" | "payment">("account");
  const [provisioningMessageIndex, setProvisioningMessageIndex] = useState(0);
  const [provisioningProgress, setProvisioningProgress] = useState(0);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const messageIntervalRef = useRef<NodeJS.Timeout | null>(null);
  // pollProvisioningStatus runs on a 5s interval and is also called directly.
  // Without this guard a slow clearInterval lets two in-flight polls both see
  // status === "active" and fire signup_complete twice for one signup.
  const signupCompleteFiredRef = useRef(false);

  // Terms acceptance state
  const [showTermsModal, setShowTermsModal] = useState(true);
  const [termsAcceptanceData, setTermsAcceptanceData] = useState<AcceptanceData | null>(null);

  // Storefront state, from the same hook the pricing page and signup use.
  // testMode drives the "this charge isn't real" banner — nobody should reach
  // a "Pay ₹4,999" button without being told. saleOpen is the gate itself:
  // reaching this page with the storefront shut means a stale tab or a
  // hand-edited URL, and the server would refuse the order anyway.
  const { saleOpen, testMode: paymentTestMode, loading: saleLoading, unlocked: saleUnlocked } = useSaleOpen();

  // Tier prices and the vertical price both come from the Mothership. This
  // page shows the customer what they are about to be charged, and the
  // Mothership prices the real order from `pricing_tiers` at create-order
  // time — reading a hardcoded array here meant the two could disagree.
  const { pricingTiers, loading: pricingLoading } = usePricingData();
  const { verticalMonthlyPrice } = useVerticalPricing();

  // Get pricing tier details
  const getPlanDetails = (planId: string) => {
    // No positional default. The old code fell back to PRICING_TIERS[1] when
    // the id did not match, which silently quoted Officer's price for an
    // unknown plan. Returning null makes the caller handle it — it already
    // renders a "plan not found" guard.
    return pricingTiers.find((t) => t.id === planId) || null;
  };

  // Razorpay hook
  const { loading: paymentLoading, error: paymentError, initiatePayment, initiateSubscription } = useRazorpay({
    onSuccess: (response) => {
      console.log("[Checkout] Payment successful:", response);
      sessionStorage.removeItem("axoix_checkout_resume");
      // Go to provisioning step instead of success
      setStep("provisioning");
    },
    onError: (error) => {
      console.error("[Checkout] Payment error:", error);
      setErrorMessage(error.message);
      setErrorKind("payment");
      setStep("error");
    },
    onDismiss: () => {
      console.log("[Checkout] Payment dismissed");
      // Stay on payment step, user can retry
    },
  });

  // Poll for provisioning status
  const pollProvisioningStatus = useCallback(async () => {
    if (!tenantId) return;

    try {
      const status = await getSubscriptionStatus(tenantId);
      console.log("[Checkout] Provisioning status:", status);

      if (status.status === "active" || status.is_active) {
        // Provisioning complete!
        setStep("success");
        // Clean up intervals
        if (pollingRef.current) clearInterval(pollingRef.current);
        if (messageIntervalRef.current) clearInterval(messageIntervalRef.current);

        // Distinct from "sign_up", which fires earlier at tenant creation —
        // before Razorpay for paid plans, so it counts abandoned payments too.
        // This one means the dashboard is actually provisioned and live.
        if (!signupCompleteFiredRef.current) {
          signupCompleteFiredRef.current = true;
          track("signup_complete", {
            tier: checkoutData?.plan,
            billing_cycle: checkoutData?.billing_cycle,
          });
        }
      }
    } catch (error) {
      console.error("[Checkout] Error polling status:", error);
      // Don't fail on polling errors, just keep trying
    }
  }, [tenantId, checkoutData?.plan, checkoutData?.billing_cycle]);

  // Start polling when entering provisioning step
  useEffect(() => {
    if (step === "provisioning") {
      // Start message rotation (1.5s per step)
      messageIntervalRef.current = setInterval(() => {
        setProvisioningMessageIndex((prev) =>
          prev < PROVISIONING_MESSAGES.length - 1 ? prev + 1 : prev
        );
        setProvisioningProgress((prev) => Math.min(prev + 12, 90));
      }, 1500);

      // Start status polling (every 5 seconds)
      pollingRef.current = setInterval(pollProvisioningStatus, 5000);
      // Also poll immediately
      pollProvisioningStatus();

      // Cleanup on unmount or step change
      return () => {
        if (pollingRef.current) clearInterval(pollingRef.current);
        if (messageIntervalRef.current) clearInterval(messageIntervalRef.current);
      };
    }
  }, [step, pollProvisioningStatus]);

  // Extract checkout data from location state, falling back to the persisted
  // draft on refresh (location.state is memory-only and is lost on reload).
  useEffect(() => {
    const state = location.state as CheckoutState | null;
    if (state && state.company_name && state.plan) {
      setCheckoutData(state);
      if (state.tenant_id) {
        setTenantId(state.tenant_id);
        sessionStorage.setItem("axoix_checkout_resume", JSON.stringify(state));
        setShowTermsModal(false);
        setStep("payment");
      }
      return;
    }

    const savedResume = sessionStorage.getItem("axoix_checkout_resume");
    if (savedResume) {
      try {
        const parsed = JSON.parse(savedResume) as CheckoutState;
        if (parsed && parsed.tenant_id && parsed.company_name) {
          setCheckoutData(parsed);
          setTenantId(parsed.tenant_id);
          setShowTermsModal(false);
          setStep("payment");
          return;
        }
      } catch {}
    }

    const draft = loadSignupDraft();
    if (
      draft &&
      draft.company_name &&
      draft.emailVerified &&
      draft.emailVerificationToken &&
      draft.admin_email &&
      draft.emailVerificationExpiresAt > Date.now()
    ) {
      // Rebuilt from the draft — admin_password was never persisted, so this
      // path only recovers the pre-payment steps; signupTenant() still needs
      // a password, which the "Session Expired" screen's "Start Again" link
      // sends the user back to /signup (pre-filled from the same draft) to supply.
      setErrorKind("account");
      setErrorMessage("no pending order|" + draft.subdomain);
      setStep("error");
      return;
    }

    navigate("/signup", { replace: true });
  }, [location.state, navigate]);

  // Handle terms acceptance
  const handleTermsAccepted = (acceptanceData: AcceptanceData) => {
    console.log("[Checkout] Terms accepted:", acceptanceData);
    setTermsAcceptanceData(acceptanceData);
    setShowTermsModal(false);
    setStep("creating");
    if (checkoutData) {
      track("begin_checkout", { plan: checkoutData.plan, billing_cycle: checkoutData.billing_cycle });
    }
  };

  // Handle terms modal close (cancel)
  const handleTermsClose = () => {
    // Go back to signup if user doesn't accept terms
    navigate("/signup", { replace: true });
  };

  // Create tenant and initiate payment
  useEffect(() => {
    const createTenantAndPay = async () => {
      // Only proceed if terms are accepted (step is "creating")
      if (!checkoutData || tenantId || step !== "creating" || !termsAcceptanceData) return;

      setErrorKind("account"); // reset — only the Razorpay leg below can upgrade this to "payment"
      try {
        // Parse name into first and last
        const nameParts = checkoutData.admin_name.trim().split(" ");
        const firstName = nameParts[0] || "";
        const lastName = nameParts.slice(1).join(" ") || "";

        // Map billing cycle for API compatibility
        const apiCycle = checkoutData.billing_cycle === "yearly" ? "annual"
          : checkoutData.billing_cycle === "five_year" ? "lifetime"
          : checkoutData.billing_cycle === "ten_year" ? "lifetime"
          : checkoutData.billing_cycle; // quarterly, six_month, monthly pass through

        // Determine whether this is a fresh signup or resuming an existing pending tenant
        let currentTenantId = checkoutData.tenant_id;
        let requiresPayment = true;

        if (!currentTenantId) {
          // Create tenant first
          const response = await signupTenant({
            company_name: checkoutData.company_name,
            subdomain: checkoutData.subdomain,
            first_name: firstName,
            last_name: lastName,
            email: checkoutData.admin_email,
            email_verification_token: checkoutData.email_verification_token,
            phone: checkoutData.phone,
            password: checkoutData.admin_password,
            plan_id: checkoutData.plan,
            billing_cycle: apiCycle as "monthly" | "yearly",
            terms_accepted: true,
            privacy_accepted: true,
            // E-Signature data from terms acceptance
            signature_png: termsAcceptanceData?.signaturePng || undefined,
            signature_type: termsAcceptanceData?.signatureType || undefined,
            signature_timestamp: termsAcceptanceData?.timestamp || undefined,
            signature_ip: termsAcceptanceData?.ipAddress || undefined,
            signature_user_agent: termsAcceptanceData?.userAgent || undefined,
            sections_viewed: termsAcceptanceData?.sectionsViewed || undefined,
            legal_version: termsAcceptanceData?.legalVersion || undefined,
            primary_vertical: checkoutData.primary_vertical,
            addon_verticals: checkoutData.addon_verticals,
            referral_code: checkoutData.referral_code || undefined,
            ...getAttribution(),
            // Address & GST
            state: checkoutData.state || undefined,
            city: checkoutData.city || undefined,
            pincode: checkoutData.pincode || undefined,
            address_line: checkoutData.address_line || undefined,
            gstin: checkoutData.gstin || undefined,
          });

          currentTenantId = response.tenant_id;
          requiresPayment = response.requires_payment !== false;
        }

        if (currentTenantId) {
          clearSignupDraft();
          // The industry has been committed to the tenant. Leaving the cookie
          // behind would preselect this business's vertical if the same person
          // later signs a second, unrelated tenant up.
          clearVerticalIntent();
          setTenantId(currentTenantId);
          track("sign_up", { tier: checkoutData.plan });

          // Explorer (free) tier: already provisioned synchronously by backend
          // Skip Razorpay entirely — go straight to provisioning/success
          if (checkoutData.plan === "explorer" || !requiresPayment) {
            setStep("provisioning");
            return;
          }

          // Map billing cycle for payment API
          const paymentCycle = checkoutData.billing_cycle === "yearly" ? "annual"
            : checkoutData.billing_cycle === "five_year" ? "lifetime"
            : checkoutData.billing_cycle === "ten_year" ? "lifetime"
            : checkoutData.billing_cycle; // quarterly, six_month, monthly pass through

          // monthly/quarterly/six_month/annual are real recurring Subscriptions
          // (auto-renew, auto-commission every cycle); five_year/ten_year
          // ("lifetime" here) stay one-time Orders — large prepaid
          // commitments, not a cadence to auto-renew years later.
          try {
            if (isRecurringBillingCycle(paymentCycle)) {
              setStep("payment");
              await initiateSubscription(
                {
                  tenant_id: currentTenantId,
                  tier_id: checkoutData.plan,
                  billing_cycle: paymentCycle as RecurringBillingCycle,
                },
                {
                  name: checkoutData.admin_name,
                  email: checkoutData.admin_email,
                  phone: checkoutData.phone,
                }
              );
            } else {
              const orderData = await createOrder({
                tenant_id: currentTenantId,
                tier_id: checkoutData.plan,
                billing_cycle: paymentCycle as "monthly" | "annual" | "lifetime",
                addon_verticals: checkoutData.addon_verticals || [],
              });

              setStep("payment");
              await initiatePayment(
                {
                  tenant_id: currentTenantId,
                  tier_id: checkoutData.plan,
                  billing_cycle: paymentCycle as "monthly" | "annual" | "lifetime",
                  addon_verticals: checkoutData.addon_verticals || [],
                },
                {
                  name: checkoutData.admin_name,
                  email: checkoutData.admin_email,
                  phone: checkoutData.phone,
                },
                orderData
              );
            }
          } catch (paymentError) {
            console.error("[Checkout] Payment setup error:", paymentError);
            setErrorKind("payment");
            throw paymentError;
          }
        }
      } catch (error) {
        console.error("[Checkout] Error creating tenant:", error);
        setErrorMessage(error instanceof Error ? error.message : "Failed to create account");
        setStep("error");
      }
    };

    if (checkoutData && step === "creating" && termsAcceptanceData) {
      createTenantAndPay();
    }
  }, [checkoutData, tenantId, step, initiatePayment, initiateSubscription, termsAcceptanceData]);

  // Get plan details for display
  const planDetails = checkoutData ? getPlanDetails(checkoutData.plan) : null;

  // Render terms acceptance step
  if (step === "terms" && checkoutData) {
    return (
      <Fragment>
        <SEO title="Checkout" description="Complete your AXOIX subscription payment." path="/checkout" noindex />
        <div className="about-page inner-page">
          <div className="body_wrap o-clip">
            <Header />
            <main>
              <PageTitle pageTitle="Accept Terms" pagesub="Checkout" />
              <section
                className="contact pt-100 pb-120 bg_img"
                style={{ backgroundImage: `url(${pricingBg})` }}
              >
                <div className="container">
                  <div className="row justify-content-center">
                    <div className="col-lg-8">
                      <div
                        className="pricing-item xb-border bg_img text-center"
                        style={{ backgroundImage: `url(${cardBg})`, padding: "60px 50px" }}
                      >
                        <div className="form-heading mb-30">
                          <span style={{ fontSize: "60px", display: "block", marginBottom: "20px" }}>
                            <i className="fas fa-file-signature" style={{ color: "var(--color-primary)" }}></i>
                          </span>
                          <h3 className="title">Review & Accept Terms</h3>
                          <p className="sub-title clr-white mt-20">
                            Before proceeding with your {planDetails?.name || "AXOIX"} subscription,
                            please review and accept our legal agreements.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </main>
            <Footer />
            <Scrollbar />
          </div>
        </div>

        {/* SEAL Terms Acceptance Modal */}
        <TermsAcceptanceModal
          isOpen={showTermsModal}
          onClose={handleTermsClose}
          onAccept={handleTermsAccepted}
          userInfo={{
            name: checkoutData.admin_name,
            email: checkoutData.admin_email,
            company: checkoutData.company_name,
          }}
          planName={planDetails?.name || "AXOIX"}
        />
      </Fragment>
    );
  }

  // Render loading/creating state
  if (step === "creating" || !checkoutData) {
    return (
      <Fragment>
        <SEO title="Checkout" description="Complete your AXOIX subscription payment." path="/checkout" noindex />
        <div className="about-page inner-page">
          <div className="body_wrap o-clip">
            <Header />
            <main>
              <PageTitle pageTitle="Processing..." pagesub="Checkout" />
              <section
                className="contact pt-100 pb-120 bg_img"
                style={{ backgroundImage: `url(${pricingBg})` }}
              >
                <div className="container">
                  <div className="row justify-content-center">
                    <div className="col-lg-8">
                      <div
                        className="pricing-item xb-border bg_img text-center"
                        style={{ backgroundImage: `url(${cardBg})`, padding: "60px 50px" }}
                      >
                        <div className="form-heading mb-30">
                          <span style={{ fontSize: "60px", display: "block", marginBottom: "20px" }}>
                            <i className="fas fa-spinner fa-spin" style={{ color: "var(--color-primary)" }}></i>
                          </span>
                          <h3 className="title">Setting Up Your Dashboard</h3>
                          <p className="sub-title clr-white mt-20">
                            Please wait while we prepare your AXOIX command center...
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </main>
            <Footer />
            <Scrollbar />
          </div>
        </div>
      </Fragment>
    );
  }

  // At this point, checkoutData is guaranteed to exist (early returns above handle null cases)
  // planDetails is already computed at line 246, but TypeScript needs a type guard.
  //
  // Tiers are fetched now rather than imported, so this is null for the first
  // render of every visit. Returning null then would blank the page mid-load;
  // wait for the fetch to settle before treating it as "plan not found".
  if (!planDetails) {
    if (pricingLoading) {
      return (
        <Fragment>
          <SEO title="Checkout" description="Complete your AXOIX subscription payment." path="/checkout" noindex />
          <div className="about-page inner-page">
            <div className="body_wrap o-clip">
              <Header />
              <main>
                <PageTitle pageTitle="Checkout" pagesub="Loading" />
                <section className="contact pt-100 pb-120">
                  <div className="container text-center">
                    <i className="fas fa-spinner fa-spin" style={{ fontSize: "32px" }}></i>
                    <p className="mt-20">Loading your plan…</p>
                  </div>
                </section>
              </main>
              <Footer />
              <Scrollbar />
            </div>
          </div>
        </Fragment>
      );
    }
    return null;
  }

  // Render provisioning state with animated loading screen
  if (step === "provisioning") {
    const currentMessage = PROVISIONING_MESSAGES[provisioningMessageIndex];
    return (
      <Fragment>
        <SEO title="Checkout" description="Complete your AXOIX subscription payment." path="/checkout" noindex />
        <div className="about-page inner-page">
          <div className="body_wrap o-clip">
            <Header />
            <main>
              <PageTitle pageTitle="Building Your Dashboard" pagesub="Setup" />
              <section
                className="contact pt-100 pb-120 bg_img"
                style={{ backgroundImage: `url(${pricingBg})` }}
              >
                <div className="container">
                  <div className="row justify-content-center">
                    <div className="col-lg-8">
                      <div
                        className="pricing-item xb-border bg_img text-center"
                        style={{ backgroundImage: `url(${cardBg})`, padding: "60px 50px" }}
                      >
                        <div className="form-heading mb-30">
                          {/* Animated rocket icon */}
                          <div style={{
                            fontSize: "80px",
                            marginBottom: "30px",
                            animation: "pulse 2s ease-in-out infinite"
                          }}>
                            <i
                              className={`fas ${currentMessage.icon}`}
                              style={{
                                color: "var(--color-primary)",
                                animation: "spin 3s linear infinite"
                              }}
                            ></i>
                          </div>

                          <h3 className="title">Building Your AXOIX Dashboard</h3>

                          <p className="sub-title clr-white mt-20" style={{ fontSize: "18px" }}>
                            {currentMessage.message}
                          </p>

                          {/* Progress bar */}
                          <div style={{
                            width: "100%",
                            maxWidth: "400px",
                            margin: "30px auto",
                            background: "rgba(255, 255, 255, 0.1)",
                            borderRadius: "10px",
                            overflow: "hidden",
                            height: "8px"
                          }}>
                            <div style={{
                              width: `${provisioningProgress}%`,
                              height: "100%",
                              background: "linear-gradient(90deg, #06b6d4, #00ff97)",
                              borderRadius: "10px",
                              transition: "width 0.5s ease-out"
                            }}></div>
                          </div>

                          {/* Status steps */}
                          <div style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "12px",
                            textAlign: "left",
                            maxWidth: "350px",
                            margin: "30px auto 0"
                          }}>
                            {PROVISIONING_MESSAGES.map((msg, idx) => (
                              <div
                                key={idx}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "12px",
                                  opacity: idx <= provisioningMessageIndex ? 1 : 0.3,
                                  transition: "opacity 0.3s ease"
                                }}
                              >
                                <i
                                  className={`fas ${idx < provisioningMessageIndex ? "fa-check-circle" : idx === provisioningMessageIndex ? "fa-spinner fa-spin" : "fa-circle"}`}
                                  style={{
                                    color: idx <= provisioningMessageIndex ? "#00ff97" : "#6b7280",
                                    fontSize: "16px"
                                  }}
                                ></i>
                                <span className="clr-white" style={{ fontSize: "14px" }}>
                                  {msg.message.replace("...", "")}
                                </span>
                              </div>
                            ))}
                          </div>

                          <div style={{
                            marginTop: "40px",
                            padding: "20px",
                            background: "rgba(0, 255, 151, 0.1)",
                            borderRadius: "10px",
                            border: "1px solid rgba(0, 255, 151, 0.2)"
                          }}>
                            <p className="clr-white" style={{ margin: 0, fontSize: "14px" }}>
                              <i className="fas fa-info-circle" style={{ marginRight: "8px", color: "var(--color-primary)" }}></i>
                              This usually takes under a minute. Please don't close this page.
                            </p>
                          </div>

                          <p className="clr-white mt-30" style={{ fontSize: "13px", opacity: 0.6 }}>
                            Your dashboard: <strong>{checkoutData.subdomain}.axoix.io</strong>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Add CSS animation for spin */}
              <style>{`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
                @keyframes pulse {
                  0%, 100% { transform: scale(1); }
                  50% { transform: scale(1.05); }
                }
              `}</style>
            </main>
            <Footer />
            <Scrollbar />
          </div>
        </div>
      </Fragment>
    );
  }

  // Calculate base price from plan
  const basePrice =
    checkoutData.billing_cycle === "monthly"
      ? planDetails.monthlyPrice
      : checkoutData.billing_cycle === "quarterly"
      ? planDetails.quarterlyPrice
      : checkoutData.billing_cycle === "six_month"
      ? planDetails.sixMonthPrice
      : checkoutData.billing_cycle === "yearly"
      ? planDetails.yearlyPrice
      : checkoutData.billing_cycle === "five_year"
      ? planDetails.fiveYearPrice
      : planDetails.tenYearPrice;

  // Calculate vertical addon cost
  const addonVerticals = checkoutData.addon_verticals || [];
  const verticalCostPerMonth = addonVerticals.length > 0
    ? addonVerticals.length * (verticalMonthlyPrice ?? 0)
    : 0;

  // Multiply verticals by billing period
  const verticalMultiplier =
    checkoutData.billing_cycle === "monthly" ? 1 :
    checkoutData.billing_cycle === "quarterly" ? 3 :
    checkoutData.billing_cycle === "six_month" ? 6 :
    checkoutData.billing_cycle === "yearly" ? 12 :
    checkoutData.billing_cycle === "five_year" ? 60 : 120;
  const totalVerticalCost = verticalCostPerMonth * verticalMultiplier;

  // Subtotal (base + verticals)
  const subtotal = (basePrice || 0) + totalVerticalCost;

  // GST calculation (18%)
  const gstAmount = Math.round(subtotal * 0.18);

  // Grand total with GST
  const grandTotal = subtotal + gstAmount;

  // Legacy price variable for backwards compatibility
  const price = grandTotal;

  // Render success state
  if (step === "success") {
    return (
      <Fragment>
        <SEO title="Checkout" description="Complete your AXOIX subscription payment." path="/checkout" noindex />
        <div className="about-page inner-page">
          <div className="body_wrap o-clip">
            <Header />
            <main>
              <PageTitle pageTitle="Welcome Aboard!" pagesub="Checkout" />
              <section
                className="contact pt-100 pb-120 bg_img"
                style={{ backgroundImage: `url(${pricingBg})` }}
              >
                <div className="container">
                  <div className="row justify-content-center">
                    <div className="col-lg-8">
                      <div
                        className="pricing-item xb-border bg_img text-center"
                        style={{ backgroundImage: `url(${cardBg})`, padding: "60px 50px" }}
                      >
                        <div className="form-heading mb-30">
                          <span style={{ fontSize: "80px", display: "block", marginBottom: "20px" }}>
                            <i className="fas fa-check-circle" style={{ color: "#00ff97" }}></i>
                          </span>
                          <h3 className="title">
                            {checkoutData.plan === "explorer" ? "Your Dashboard is Ready!" : "Payment Successful!"}
                          </h3>
                          <p className="sub-title clr-white mt-20">
                            Welcome to the {planDetails.name} tier! Your AXOIX dashboard at{" "}
                            <strong>{checkoutData.subdomain}.axoix.io</strong> is ready.
                          </p>
                          <div
                            className="mt-30 p-25"
                            style={{
                              background: "rgba(0, 255, 151, 0.1)",
                              borderRadius: "10px",
                              border: "1px solid rgba(0, 255, 151, 0.3)",
                            }}
                          >
                            <h4 className="clr-white mb-15">Order Summary</h4>
                            <div className="ul_li_between clr-white mb-10">
                              <span>Plan</span>
                              <strong>{planDetails.name}</strong>
                            </div>
                            <div className="ul_li_between clr-white mb-10">
                              <span>Billing</span>
                              <strong>{checkoutData.plan === "explorer" ? "Free Forever" : checkoutData.billing_cycle}</strong>
                            </div>
                            <div className="ul_li_between clr-white">
                              <span>{checkoutData.plan === "explorer" ? "Cost" : "Amount Paid"}</span>
                              <strong style={{ color: "var(--color-primary)" }}>
                                {checkoutData.plan === "explorer" ? "FREE" : formatPrice(price || 0)}
                              </strong>
                            </div>
                          </div>
                        </div>
                        {/* /login, not the bare root — since 2026-09-11 POC serves the
                            tenant's public storefront at the bare subdomain root and
                            moved the dashboard entry point to an explicit /login path.
                            Reported BR-088 / fixed BR-090 (communication repo): a
                            customer who just paid and clicks this landed on their own
                            empty storefront instead of a place to sign in. Matches the
                            "Login to your dashboard" link below, which already had this
                            correct. */}
                        <a
                          href={`https://${checkoutData.subdomain}.axoix.io/login`}
                          className="thm-btn agency-btn"
                        >
                          <span className="text">Launch Dashboard</span>
                          <span className="arrow">
                            <span className="arrow-icon">
                              <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                                <rect x="5.06592" y="19.9785" width="20.5712" height="2.61221" transform="rotate(-40.2798 5.06592 19.9785)" fill="white"/>
                                <rect x="7.97095" y="7.24463" width="2.61221" height="2.61221" transform="rotate(-40.2798 7.97095 7.24463)" fill="white"/>
                                <rect x="11.6523" y="7.54834" width="2.61221" height="2.61221" transform="rotate(-40.2798 11.6523 7.54834)" fill="white"/>
                                <rect x="15.334" y="7.85205" width="2.61221" height="2.61221" transform="rotate(-40.2798 15.334 7.85205)" fill="white"/>
                                <rect x="18.7119" y="11.8374" width="2.61221" height="2.61221" transform="rotate(-40.2798 18.7119 11.8374)" fill="white"/>
                                <rect x="18.4084" y="15.52" width="2.61221" height="2.61221" transform="rotate(-40.2798 18.4084 15.52)" fill="white"/>
                                <rect x="18.104" y="19.2012" width="2.61221" height="2.61221" transform="rotate(-40.2798 18.104 19.2012)" fill="white"/>
                              </svg>
                              <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                                <rect x="5.06592" y="19.9785" width="20.5712" height="2.61221" transform="rotate(-40.2798 5.06592 19.9785)" fill="white"/>
                                <rect x="7.97095" y="7.24463" width="2.61221" height="2.61221" transform="rotate(-40.2798 7.97095 7.24463)" fill="white"/>
                                <rect x="11.6523" y="7.54834" width="2.61221" height="2.61221" transform="rotate(-40.2798 11.6523 7.54834)" fill="white"/>
                                <rect x="15.334" y="7.85205" width="2.61221" height="2.61221" transform="rotate(-40.2798 15.334 7.85205)" fill="white"/>
                                <rect x="18.7119" y="11.8374" width="2.61221" height="2.61221" transform="rotate(-40.2798 18.7119 11.8374)" fill="white"/>
                                <rect x="18.4084" y="15.52" width="2.61221" height="2.61221" transform="rotate(-40.2798 18.4084 15.52)" fill="white"/>
                                <rect x="18.104" y="19.2012" width="2.61221" height="2.61221" transform="rotate(-40.2798 18.104 19.2012)" fill="white"/>
                              </svg>
                            </span>
                          </span>
                        </a>
                        <p className="clr-white mt-20" style={{ fontSize: "14px", opacity: 0.7 }}>
                          Check your email for login credentials and getting started guide.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </main>
            <Footer />
            <Scrollbar />
          </div>
        </div>
      </Fragment>
    );
  }

  // Check if error is "Email already registered" and extract subdomain
  const isEmailAlreadyRegistered = errorMessage?.toLowerCase().includes("already registered");
  const existingSubdomain = errorMessage?.includes("|") ? errorMessage.split("|")[1] : null;

  // Render error state
  if (step === "error") {
    // Special handling for existing accounts
    if (isEmailAlreadyRegistered) {
      return (
        <Fragment>
          <SEO title="Checkout" description="Complete your AXOIX subscription payment." path="/checkout" noindex />
          <div className="about-page inner-page">
            <div className="body_wrap o-clip">
              <Header />
              <main>
                <PageTitle pageTitle="Account Exists" pagesub="Login" />
                <section
                  className="contact pt-100 pb-120 bg_img"
                  style={{ backgroundImage: `url(${pricingBg})` }}
                >
                  <div className="container">
                    <div className="row justify-content-center">
                      <div className="col-lg-8">
                        <div
                          className="pricing-item xb-border bg_img text-center"
                          style={{ backgroundImage: `url(${cardBg})`, padding: "60px 50px" }}
                        >
                          <div className="form-heading mb-30">
                            <span style={{ fontSize: "80px", display: "block", marginBottom: "20px" }}>
                              <i className="fas fa-user-check" style={{ color: "#00ff97" }}></i>
                            </span>
                            <h3 className="title">Welcome Back!</h3>
                            <p className="sub-title clr-white mt-20">
                              An account with <strong style={{ color: "var(--color-primary)" }}>{checkoutData?.admin_email}</strong> already exists.
                            </p>
                            <p className="clr-white mt-15" style={{ fontSize: "16px", opacity: 0.8 }}>
                              Login to your dashboard to upgrade your plan or manage your subscription.
                            </p>
                          </div>
                          <div className="ul_li_center" style={{ gap: "15px", flexWrap: "wrap" }}>
                            {existingSubdomain && (
                              <a
                                href={`https://${existingSubdomain}.axoix.io/login`}
                                className="thm-btn agency-btn"
                                style={{ fontSize: "18px", padding: "15px 40px" }}
                              >
                                <span className="text">Login to Dashboard</span>
                              </a>
                            )}
                            <Link to="/signup" className="thm-btn agency-btn" style={{ background: "transparent", border: "1px solid var(--color-primary)" }}>
                              <span className="text">Use Different Email</span>
                            </Link>
                          </div>
                          <p className="clr-white mt-30" style={{ fontSize: "14px", opacity: 0.7 }}>
                            Need help? Contact{" "}
                            <a href="mailto:support@axoix.com" style={{ color: "var(--color-primary)" }}>
                              support@axoix.com
                            </a>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </main>
              <Footer />
              <Scrollbar />
            </div>
          </div>
        </Fragment>
      );
    }

    // Check if this is a state-related error that requires starting over
    const currentError = errorMessage || paymentError || "";
    const isStateError = currentError.toLowerCase().includes("no pending order") ||
                        currentError.toLowerCase().includes("tenant not found") ||
                        currentError.toLowerCase().includes("order id mismatch");
    // Only a failure inside the actual Razorpay leg counts as a payment issue — a crash
    // during account creation (any tier, including paid ones before checkout is reached)
    // should never be presented to the user as a payment problem.
    const isAccountError = errorKind !== "payment";

    return (
      <Fragment>
        <SEO title="Checkout" description="Complete your AXOIX subscription payment." path="/checkout" noindex />
        <div className="about-page inner-page">
          <div className="body_wrap o-clip">
            <Header />
            <main>
              <PageTitle pageTitle={isAccountError ? "Signup Issue" : "Payment Issue"} pagesub="Checkout" />
              <section
                className="contact pt-100 pb-120 bg_img"
                style={{ backgroundImage: `url(${pricingBg})` }}
              >
                <div className="container">
                  <div className="row justify-content-center">
                    <div className="col-lg-8">
                      <div
                        className="pricing-item xb-border bg_img text-center"
                        style={{ backgroundImage: `url(${cardBg})`, padding: "60px 50px" }}
                      >
                        <div className="form-heading mb-30">
                          <span style={{ fontSize: "80px", display: "block", marginBottom: "20px" }}>
                            <i className="fas fa-exclamation-triangle" style={{ color: "#ff4444" }}></i>
                          </span>
                          <h3 className="title">{isStateError ? "Session Expired" : "Something Went Wrong"}</h3>
                          <p className="sub-title clr-white mt-20">
                            {isStateError
                              ? "Your checkout session has expired or the payment state was lost. Please start the signup process again."
                              : currentError || (isAccountError
                                ? "We couldn't create your account. Please try again."
                                : "We couldn't complete your payment. Please try again.")}
                          </p>
                        </div>
                        <div className="ul_li_center" style={{ gap: "15px", flexWrap: "wrap" }}>
                          {!isStateError && tenantId && (
                            <button
                              onClick={() => {
                                setStep("payment");
                                setErrorMessage("");
                                const retryCycle = checkoutData.billing_cycle === "yearly" ? "annual"
                                  : checkoutData.billing_cycle === "five_year" || checkoutData.billing_cycle === "ten_year" ? "lifetime"
                                  : checkoutData.billing_cycle;
                                const retryUserInfo = {
                                  name: checkoutData.admin_name,
                                  email: checkoutData.admin_email,
                                  phone: checkoutData.phone,
                                };
                                if (isRecurringBillingCycle(retryCycle)) {
                                  initiateSubscription(
                                    {
                                      tenant_id: tenantId,
                                      tier_id: checkoutData.plan,
                                      billing_cycle: retryCycle as RecurringBillingCycle,
                                    },
                                    retryUserInfo
                                  );
                                } else {
                                  initiatePayment(
                                    {
                                      tenant_id: tenantId,
                                      tier_id: checkoutData.plan,
                                      billing_cycle: retryCycle as "monthly" | "quarterly" | "six_month" | "annual" | "lifetime",
                                      addon_verticals: checkoutData.addon_verticals || [],
                                    },
                                    retryUserInfo
                                  );
                                }
                              }}
                              className="thm-btn agency-btn"
                            >
                              <span className="text">Try Again</span>
                            </button>
                          )}
                          <Link
                            to="/signup"
                            state={isStateError ? {
                              // Pre-fill form with existing data for state errors
                              company_name: checkoutData.company_name,
                              subdomain: checkoutData.subdomain,
                              admin_email: checkoutData.admin_email,
                              admin_name: checkoutData.admin_name,
                              phone: checkoutData.phone,
                              plan: checkoutData.plan,
                              billing_cycle: checkoutData.billing_cycle,
                              primary_vertical: checkoutData.primary_vertical,
                            } : undefined}
                            className="thm-btn agency-btn"
                            style={isStateError ? {} : { background: "transparent", border: "1px solid var(--color-primary)" }}
                          >
                            <span className="text">{isStateError ? "Start Again" : "Back to Signup"}</span>
                          </Link>
                        </div>
                        <p className="clr-white mt-30" style={{ fontSize: "14px", opacity: 0.7 }}>
                          Need help? Contact us at{" "}
                          <a href="mailto:support@axoix.com" style={{ color: "var(--color-primary)" }}>
                            support@axoix.com
                          </a>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </main>
            <Footer />
            <Scrollbar />
          </div>
        </div>
      </Fragment>
    );
  }

  // Render payment step (waiting for Razorpay modal)
  return (
    <Fragment>
      <SEO title="Checkout" description="Complete your AXOIX subscription payment." path="/checkout" noindex />
      <div className="about-page inner-page">
        <div className="body_wrap o-clip">
          <Header />
          <main>
            <PageTitle pageTitle="Complete Payment" pagesub="Checkout" />
            <section
              className="contact pt-100 pb-120 bg_img"
              style={{ backgroundImage: `url(${pricingBg})` }}
            >
              <div className="container">
                <div className="row justify-content-center">
                  <div className="col-lg-10">
                    <div className="row">
                      {/* Order Summary */}
                      <div className="col-lg-5 mb-30">
                        <div
                          className="pricing-item xb-border bg_img"
                          style={{ backgroundImage: `url(${cardBg})`, padding: "30px" }}
                        >
                          <h4 className="clr-white mb-20">Order Summary</h4>
                          <div
                            style={{
                              background: "rgba(0, 255, 151, 0.1)",
                              borderRadius: "10px",
                              padding: "20px",
                              border: "1px solid rgba(0, 255, 151, 0.3)",
                            }}
                          >
                            <div className="ul_li_between clr-white mb-15">
                              <span>Plan</span>
                              <strong>{planDetails.name}</strong>
                            </div>
                            <div className="ul_li_between clr-white mb-15">
                              <span>Billing Cycle</span>
                              <strong style={{ textTransform: "capitalize" }}>{checkoutData.billing_cycle.replace('_', ' ')}</strong>
                            </div>
                            <div className="ul_li_between clr-white mb-15">
                              <span>Primary Industry</span>
                              <strong style={{ textTransform: "capitalize" }}>{checkoutData.primary_vertical}</strong>
                            </div>
                            <div className="ul_li_between clr-white mb-15">
                              <span>Dashboard URL</span>
                              <strong style={{ fontSize: "12px" }}>{checkoutData.subdomain}.axoix.io</strong>
                            </div>
                            <hr style={{ borderColor: "rgba(255,255,255,0.2)", margin: "15px 0" }} />
                            <div className="ul_li_between clr-white mb-10">
                              <span>Base Plan</span>
                              <strong>{formatPrice(basePrice || 0)}</strong>
                            </div>
                            {addonVerticals.length > 0 && (
                              <div className="ul_li_between mb-10" style={{ color: "var(--color-primary)" }}>
                                <span>{addonVerticals.length} Addon Vertical(s) <span style={{ opacity: 0.7 }}>(60% off!)</span></span>
                                <strong>{formatPrice(totalVerticalCost)}</strong>
                              </div>
                            )}
                            <div className="ul_li_between clr-white mb-10">
                              <span>Subtotal</span>
                              <strong>{formatPrice(subtotal)}</strong>
                            </div>
                            <div className="ul_li_between mb-10" style={{ color: "#ffc800" }}>
                              <span>GST (18%)</span>
                              <strong>{formatPrice(gstAmount)}</strong>
                            </div>
                            <hr style={{ borderColor: "rgba(255,255,255,0.2)", margin: "15px 0" }} />
                            <div className="ul_li_between clr-white">
                              <span style={{ fontSize: "18px" }}>Total (incl. GST)</span>
                              <strong style={{ fontSize: "24px", color: "var(--color-primary)" }}>
                                {formatPrice(grandTotal)}
                              </strong>
                            </div>
                            <p className="clr-white mt-15" style={{ fontSize: "11px", opacity: 0.6, lineHeight: 1.5 }}>
                              Tax invoice issued by <strong>Axoix Technologies Private Limited</strong>,{" "}
                              GSTIN <strong>{STATUTORY.gstin}</strong> (Delhi). Add your GSTIN in the
                              previous step to claim input tax credit.
                            </p>
                          </div>

                          <div className="mt-20">
                            <h5 className="clr-white mb-15">What&apos;s included:</h5>
                            <ul style={{ listStyle: "none", padding: 0 }}>
                              {planDetails.features.slice(0, 5).map((feature, idx) =>
                                feature.included ? (
                                  <li
                                    key={idx}
                                    className="clr-white mb-10"
                                    style={{ fontSize: "14px" }}
                                  >
                                    <i className="fas fa-check" style={{ color: "#00ff97", marginRight: "10px" }}></i>
                                    {feature.text}
                                  </li>
                                ) : null
                              )}
                            </ul>
                          </div>
                        </div>
                      </div>

                      {/* Payment Section */}
                      <div className="col-lg-7">
                        <div
                          className="pricing-item xb-border bg_img"
                          style={{ backgroundImage: `url(${cardBg})`, padding: "30px" }}
                        >
                          <h4 className="clr-white mb-20">Secure Payment</h4>

                          {paymentLoading ? (
                            <div className="text-center" style={{ padding: "40px 0" }}>
                              <i
                                className="fas fa-spinner fa-spin"
                                style={{ fontSize: "40px", color: "var(--color-primary)", marginBottom: "20px" }}
                              ></i>
                              <p className="clr-white">Processing your payment...</p>
                              <p className="clr-white" style={{ fontSize: "14px", opacity: 0.7 }}>
                                Please complete the payment in the Razorpay window.
                              </p>
                            </div>
                          ) : !saleLoading && !saleOpen ? (
                            // The storefront closed under this session — a tab left
                            // open across the switch, or a hand-edited URL. Say so
                            // plainly instead of opening a Razorpay modal that the
                            // server will refuse the moment the order is created.
                            <div className="text-center" style={{ padding: "20px 0" }}>
                              <div
                                style={{
                                  background: "rgba(255, 170, 0, 0.12)",
                                  border: "1px solid rgba(255, 170, 0, 0.45)",
                                  borderRadius: "10px",
                                  padding: "20px",
                                  color: "#ffc861",
                                  fontSize: "14px",
                                  textAlign: "left",
                                }}
                              >
                                <strong>
                                  <i className="fas fa-lock" style={{ marginRight: "8px" }}></i>
                                  Paid plans aren't open yet
                                </strong>
                                <div style={{ marginTop: "8px" }}>
                                  Your account has been created — nothing was charged. Start on
                                  Explorer (free forever) and upgrade from your dashboard once
                                  paid plans open.
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="text-center" style={{ padding: "20px 0" }}>
                              {saleUnlocked && (
                                <div
                                  style={{
                                    background: "rgba(160, 120, 255, 0.12)",
                                    border: "1px solid rgba(160, 120, 255, 0.45)",
                                    borderRadius: "10px",
                                    padding: "15px 18px",
                                    marginBottom: "20px",
                                    color: "#c9b3ff",
                                    fontSize: "13px",
                                    textAlign: "left",
                                  }}
                                >
                                  <strong>
                                    <i className="fas fa-key" style={{ marginRight: "8px" }}></i>
                                    PRE-LAUNCH ACCESS
                                  </strong>
                                  <div style={{ marginTop: "6px" }}>
                                    Checkout is open to you via an unlock link while paid plans
                                    are still closed to the public.
                                    {!paymentTestMode && " This is a real charge at the real price."}
                                  </div>
                                </div>
                              )}
                              {paymentTestMode && (
                                <div
                                  style={{
                                    background: "rgba(255, 170, 0, 0.12)",
                                    border: "1px solid rgba(255, 170, 0, 0.45)",
                                    borderRadius: "10px",
                                    padding: "15px 18px",
                                    marginBottom: "20px",
                                    color: "#ffc861",
                                    fontSize: "13px",
                                    textAlign: "left",
                                  }}
                                >
                                  <strong>
                                    <i className="fas fa-flask" style={{ marginRight: "8px" }}></i>
                                    TEST MODE
                                  </strong>
                                  <div style={{ marginTop: "6px" }}>
                                    This checkout runs on a Razorpay <strong>test</strong> key. No
                                    money is charged, no invoice is raised and nothing is posted to
                                    accounting. Your workspace is still provisioned for real. Use
                                    Razorpay's test card <code>4111 1111 1111 1111</code>, any
                                    future expiry, any CVV.
                                  </div>
                                </div>
                              )}
                              <div
                                style={{
                                  background: "rgba(255, 255, 255, 0.05)",
                                  borderRadius: "10px",
                                  padding: "30px",
                                  marginBottom: "20px",
                                }}
                              >
                                <i
                                  className="fas fa-lock"
                                  style={{ fontSize: "30px", color: "var(--color-primary)", marginBottom: "15px" }}
                                ></i>
                                <p className="clr-white mb-10">
                                  Click below to complete your secure payment via Razorpay
                                </p>
                                <p className="clr-white" style={{ fontSize: "12px", opacity: 0.7 }}>
                                  Supports UPI, Cards, Net Banking, Wallets
                                </p>
                              </div>

                              <button
                                onClick={() => {
                                  const effectiveTenantId = tenantId || checkoutData.tenant_id;
                                  if (effectiveTenantId) {
                                    const payCycle = checkoutData.billing_cycle === "yearly" ? "annual"
                                      : checkoutData.billing_cycle === "five_year" || checkoutData.billing_cycle === "ten_year" ? "lifetime"
                                      : checkoutData.billing_cycle;
                                    const payUserInfo = {
                                      name: checkoutData.admin_name,
                                      email: checkoutData.admin_email,
                                      phone: checkoutData.phone,
                                    };
                                    if (isRecurringBillingCycle(payCycle)) {
                                      initiateSubscription(
                                        {
                                          tenant_id: effectiveTenantId,
                                          tier_id: checkoutData.plan,
                                          billing_cycle: payCycle as RecurringBillingCycle,
                                        },
                                        payUserInfo
                                      );
                                    } else {
                                      initiatePayment(
                                        {
                                          tenant_id: effectiveTenantId,
                                          tier_id: checkoutData.plan,
                                          billing_cycle: payCycle as "monthly" | "annual" | "lifetime",
                                          addon_verticals: checkoutData.addon_verticals || [],
                                        },
                                        payUserInfo
                                      );
                                    }
                                  }
                                }}
                                className="thm-btn agency-btn w-100"
                                style={{ justifyContent: "center" }}
                              >
                                <span className="text">
                                  <i className="fas fa-credit-card" style={{ marginRight: "10px" }}></i>
                                  Pay {formatPrice(price || 0)}
                                </span>
                              </button>

                              <div className="mt-20" style={{ display: "flex", justifyContent: "center", gap: "15px", opacity: 0.7 }}>
                                <img src="https://razorpay.com/assets/razorpay-glyph.svg" alt="Razorpay" style={{ height: "20px", filter: "brightness(0) invert(1)" }} />
                                <img src="https://upload.wikimedia.org/wikipedia/commons/e/e1/UPI-Logo-vector.svg" alt="UPI" style={{ height: "20px", filter: "brightness(0) invert(1)" }} />
                              </div>
                            </div>
                          )}

                          {!paymentLoading && (
                            <div className="mt-15 text-center">
                              <button
                                type="button"
                                onClick={() => navigate("/signup")}
                                className="thm-btn agency-btn"
                                style={{
                                  background: "transparent",
                                  border: "1px solid rgba(255, 255, 255, 0.35)",
                                  padding: "10px 24px",
                                }}
                              >
                                <span className="text">
                                  <i className="fas fa-arrow-left me-2"></i>
                                  Cancel and go back
                                </span>
                              </button>
                            </div>
                          )}

                          <div className="mt-20 text-center">
                            <p className="clr-white" style={{ fontSize: "12px", opacity: 0.7 }}>
                              <i className="fas fa-shield-alt" style={{ marginRight: "5px" }}></i>
                              256-bit SSL Encrypted | PCI DSS Compliant
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </main>
          <Footer />
          <Scrollbar />
        </div>
      </div>
    </Fragment>
  );
};

export default CheckoutPage;
